import { computed, inject, Injectable, signal } from '@angular/core';
import { ISucursalData } from '@core/interfaces/entidades/sucursal.entidad';
import { SucursalApiService } from '@core/services/api/sucursal-api.service';
import { ToastService } from './toast.service';
import { ImpresoraService } from './impresora.service';
import { safeIGV } from '@shared/utils/convertidor.utils';

@Injectable({ providedIn: 'root' })
export class SucursalService {
  private sucursalApi = inject(SucursalApiService);
  private toastService = inject(ToastService);

  private readonly STORAGE_KEY_SUCURSAL = 'ventaclick_ultima_sucursal_id';
  private readonly STORAGE_KEY_PRINTER = 'ventaclick_ultima_impresora_id';
  private readonly STORAGE_KEY_IGV = 'ventaclick_ultimo_igv';
  private readonly impresoraService = inject(ImpresoraService);

  sucursales = signal<ISucursalData[]>([]);
  activeSucursal = signal<ISucursalData | null>(null);
  loading = signal(false);
  sucursalRestringidaId = signal<number | null>(null);

  canSelectSucursal = computed(() => {
    return this.sucursalRestringidaId() == null;
  });

  sucursalReady = computed(() => {
    const ready = !this.loading() && this.sucursales().length > 0 && this.activeSucursal() !== null;
    return ready == true
      ? {
          sucursalId: this.activeSucursal()?.id,
          ready,
        }
      : false;
  });

  /**
   * Carga la lista de sucursales disponibles para el usuario.
   * Si el usuario tiene sucursal restringida, solo se carga esa y se auto-selecciona.
   */
  ensureSucursalesLoaded() {
    if (this.loading() || this.sucursales().length > 0) return;
    this.loading.set(true);
    this.sucursalApi.listar().subscribe({
      next: (sucursales) => {
        this.sucursales.set(sucursales);

        const sucursalRestringida = this.sucursalRestringidaId();

        if (sucursalRestringida != null) {
          const restringida = sucursales.find((s) => s.id === sucursalRestringida);
          if (restringida) {
            this.activeSucursal.set(restringida);
            this.guardarUltimaSucursalId(restringida.id);
          }
          return;
        }

        const ultimaSucursalId = this.getUltimaSucursalId();
        if (ultimaSucursalId) {
          const ultima = sucursales.find((s) => s.id === ultimaSucursalId);
          if (ultima) {
            this.activeSucursal.set(ultima);
            this.impresoraService.setImpresoraSeleccionada(ultima.id);
          } else {
            this.seleccionarSucursalPredeterminada(sucursales);
          }
        } else {
          this.seleccionarSucursalPredeterminada(sucursales);
        }
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Sucursales',
          err?.error?.message || 'No se pudieron cargar las sucursales.',
        );
      },
      complete: () => this.loading.set(false),
    });
  }

  private seleccionarSucursalPredeterminada(sucursales: ISucursalData[]) {
    // Si solo hay una sucursal, seleccionarla automáticamente
    if (sucursales.length === 1) {
      this.activeSucursal.set(sucursales[0]);
      this.guardarUltimaSucursalId(sucursales[0].id);
    } else {
      // Si hay una sucursal principal, seleccionarla automáticamente
      const principal = sucursales.find((s) => s.es_principal);
      if (principal) {
        this.activeSucursal.set(principal);
        this.guardarUltimaSucursalId(principal.id);
      }
    }
  }

  private getUltimaSucursalId(): number | null {
    try {
      const id = localStorage.getItem(this.STORAGE_KEY_SUCURSAL);
      return id ? parseInt(id, 10) : null;
    } catch {
      return null;
    }
  }

  getUltimoIGV(): number | null {
    try {
      const igv = localStorage.getItem(this.STORAGE_KEY_IGV);
      return safeIGV(igv);
    } catch {
      return null;
    }
  }

  private guardarUltimaSucursalId(id: number) {
    try {
      localStorage.setItem(this.STORAGE_KEY_SUCURSAL, id.toString());
    } catch {
      // Silenciar errores de localStorage (ej: QuotaExceededError)
    }
  }

  guardarUltimoIGV(igv: number) {
    try {
      console.log('Guardando último IGV:', igv);
      localStorage.setItem(this.STORAGE_KEY_IGV, safeIGV(igv).toString());
    } catch {
      // Silenciar errores de localStorage (ej: QuotaExceededError)
    }
  }

  private getKeyImpresora(): string {
    return `${this.STORAGE_KEY_PRINTER}_${this.activeSucursal()?.id ?? 'default'}`;
  }

  getUltimaImpresoraId(): number | null {
    try {
      const id = localStorage.getItem(this.getKeyImpresora());
      return id ? parseInt(id, 10) : null;
    } catch {
      return null;
    }
  }

  setUltimaImpresoraId(id: number | null) {
    try {
      if (id === null) {
        localStorage.removeItem(this.getKeyImpresora());
      } else {
        localStorage.setItem(this.getKeyImpresora(), id.toString());
      }
    } catch {
      // Silenciar errores de localStorage (ej: QuotaExceededError)
    }
  }

  setActiveById(id: number) {
    const currentList = this.sucursales();
    const found = currentList.find((s) => s.id === id);
    if (found) {
      this.activeSucursal.set(found);
      this.guardarUltimaSucursalId(id);
      return true;
    }
    return false;
  }

  syncSucursalActive(partialData: ISucursalData) {
    const sucursal = { ...this.activeSucursal(), ...partialData };
    this.activeSucursal.set(sucursal);
    this.guardarUltimaSucursalId(sucursal.id);
    this.sucursales.update((arr) => arr.map((s) => (s.id === sucursal.id ? sucursal : s)));
    this.toastService.show(
      'bottom-right',
      'success',
      'Sucursal cambiada',
      `Operando en: ${sucursal.nombre}`,
    );
  }

  clear() {
    this.sucursales.set([]);
    this.activeSucursal.set(null);
    this.loading.set(false);
    localStorage.removeItem(this.STORAGE_KEY_SUCURSAL);
    localStorage.removeItem(this.STORAGE_KEY_PRINTER);
    localStorage.removeItem(this.STORAGE_KEY_IGV);
  }

  sucursalPorID(sucursalId: number): ISucursalData | null {
    const sucursal = this.sucursales().find((s) => s.id === sucursalId) || null;
    return sucursal;
  }
}
